import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Header from './Header';
import Footer from './Footer';
import PortfolioPage from './PortfolioPage';
import './PortfolioBuilderLanding.css';

const HERO_IMG = '/images/portfolio-builder/hero-portfolio-stage.png';
const SHELF_IMG = '/images/portfolio-builder/template-shelf.png';
const PREVIEW_IMG = '/images/portfolio-builder/live-preview-mirror.png';
const SHARE_IMG = '/images/portfolio-builder/public-share-link.png';

/* ---------- Three.js floating cards canvas ---------- */
const FloatingCardsCanvas = () => {
  const mountRef = useRef(null);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 80;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const sprite = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.35, 'rgba(255,105,180,0.95)');
      g.addColorStop(0.7, 'rgba(224,163,255,0.4)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    })();

    // Five floating "template cards" — colored wireframe planes
    const cardColors = [0xff69b4, 0xe0a3ff, 0x9370db, 0x00ffff, 0xff8c00];
    const cards = cardColors.map((color, i) => {
      const geo = new THREE.PlaneGeometry(14, 18, 1, 1);
      const edges = new THREE.EdgesGeometry(geo);
      const mat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.7 });
      const line = new THREE.LineSegments(edges, mat);
      line.position.x = (i - 2) * 8;
      line.position.y = Math.sin(i) * 4;
      line.position.z = -Math.abs(i - 2) * 4;
      line.rotation.y = (i - 2) * 0.18;
      scene.add(line);
      return { line, mat, geo, edges, baseY: line.position.y, phase: i * 0.6 };
    });

    // ambient particle field
    const fieldCount = 220;
    const fieldPos = new Float32Array(fieldCount * 3);
    const fieldSpeeds = new Float32Array(fieldCount);
    for (let i = 0; i < fieldCount; i++) {
      fieldPos[i * 3] = (Math.random() - 0.5) * 180;
      fieldPos[i * 3 + 1] = (Math.random() - 0.5) * 110;
      fieldPos[i * 3 + 2] = (Math.random() - 0.5) * 60;
      fieldSpeeds[i] = 0.04 + Math.random() * 0.10;
    }
    const fieldGeo = new THREE.BufferGeometry();
    fieldGeo.setAttribute('position', new THREE.BufferAttribute(fieldPos, 3));
    const fieldMat = new THREE.PointsMaterial({
      size: 1.1, map: sprite, transparent: true,
      depthWrite: false, blending: THREE.AdditiveBlending,
      color: 0xffffff, opacity: 0.5,
    });
    const field = new THREE.Points(fieldGeo, fieldMat);
    scene.add(field);

    let raf;
    let mx = 0, my = 0, t = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);
    const onResize = () => {
      width = mount.clientWidth; height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      t += 0.012;
      cards.forEach((c, i) => {
        c.line.position.y = c.baseY + Math.sin(t + c.phase) * 1.5;
        c.line.rotation.y = (i - 2) * 0.18 + Math.sin(t * 0.4 + c.phase) * 0.05;
      });

      const fp = fieldGeo.attributes.position.array;
      for (let j = 0; j < fieldCount; j++) {
        fp[j * 3 + 1] += fieldSpeeds[j] * 0.5;
        if (fp[j * 3 + 1] > 55) fp[j * 3 + 1] = -55;
      }
      fieldGeo.attributes.position.needsUpdate = true;

      camera.position.x += (mx * 6 - camera.position.x) * 0.04;
      camera.position.y += (-my * 4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      cards.forEach((c) => { c.geo.dispose(); c.edges.dispose(); c.mat.dispose(); });
      fieldGeo.dispose(); fieldMat.dispose();
      sprite.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="pbl-hero-canvas" aria-hidden="true" />;
};

/* ---------- Template carousel demo (5 templates auto-cycle) ---------- */
const PBL_TEMPLATES = [
  { id: 'minimal', name: 'Minimal', accent: '#e0a3ff', layout: 'minimal' },
  { id: 'creative', name: 'Creative', accent: '#ff69b4', layout: 'creative' },
  { id: 'gallery', name: 'Gallery', accent: '#9370db', layout: 'gallery' },
  { id: 'magazine', name: 'Magazine', accent: '#00ffff', layout: 'magazine' },
  { id: 'pro', name: 'Professional', accent: '#ff8c00', layout: 'pro' },
];

const TemplateMorphDemo = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % PBL_TEMPLATES.length), 2800);
    return () => clearInterval(t);
  }, []);
  const tpl = PBL_TEMPLATES[idx];

  return (
    <section className="pbl-section">
      <div className="pbl-section-grid pbl-flip">
        <div className="pbl-template-stage">
          <div className="pbl-template-glow" style={{ background: `radial-gradient(circle, ${tpl.accent}33 0%, transparent 70%)` }} />
          {PBL_TEMPLATES.map((t, i) => {
            const offset = (i - idx + PBL_TEMPLATES.length) % PBL_TEMPLATES.length;
            return (
              <div
                key={t.id}
                className={`pbl-template-card layout-${t.layout}`}
                style={{
                  '--accent': t.accent,
                  transform: `translate(${offset * 14}px, ${offset * 18}px) rotate(${offset * 2.5}deg) scale(${1 - offset * 0.04})`,
                  zIndex: PBL_TEMPLATES.length - offset,
                  opacity: offset === 0 ? 1 : 0.55 - offset * 0.10,
                }}
              >
                <div className="pbl-template-head" />
                <div className="pbl-template-body">
                  <div className="pbl-template-bar w90" />
                  <div className="pbl-template-bar w70" />
                  <div className="pbl-template-bar w80" />
                  <div className="pbl-template-bar w50" />
                  <div className="pbl-template-bar w85" />
                  <div className="pbl-template-bar w60" />
                </div>
              </div>
            );
          })}
        </div>

        <div className="pbl-section-text">
          <span className="pbl-eyebrow">FIVE HAND-TUNED TEMPLATES</span>
          <h2 className="pbl-h2">
            One profile, <span className="pbl-grad-text">five looks</span>.
          </h2>
          <p className="pbl-lead">
            Minimal, Creative, Gallery, Magazine, Professional — each template re-arranges your
            content to match a different vibe. Switch styles mid-flow; your data stays put,
            only the layout breathes.
          </p>
          <div className="pbl-template-pills">
            {PBL_TEMPLATES.map((t, i) => (
              <button
                key={t.id}
                type="button"
                className={`pbl-pill ${i === idx ? 'active' : ''}`}
                style={{ '--accent': t.accent }}
                onClick={() => setIdx(i)}
              >
                {t.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Live preview demo (split editor + preview) ---------- */
const LivePreviewDemo = () => {
  const ref = useRef(null);
  const [name, setName] = useState('Jane Doe');
  const [role, setRole] = useState('Senior Software Engineer');
  const [hue, setHue] = useState('#ff69b4');

  return (
    <section className="pbl-section" ref={ref}>
      <div className="pbl-section-grid">
        <div className="pbl-section-text">
          <span className="pbl-eyebrow">LIVE PREVIEW</span>
          <h2 className="pbl-h2">
            Edit on the left. <span className="pbl-grad-text">Watch it render</span> on the right.
          </h2>
          <p className="pbl-lead">
            Every change is instant. Type your headline, swap the accent color, switch templates
            — the preview updates in the same frame. No "save and refresh." No "what will it look
            like."
          </p>
          <ul className="pbl-bullet-list">
            <li><span className="pbl-dot" />Sub-frame WYSIWYG render</li>
            <li><span className="pbl-dot" />Mobile + desktop preview toggle</li>
            <li><span className="pbl-dot" />Auto-saves every keystroke</li>
          </ul>
        </div>

        <div className="pbl-preview-card">
          <div className="pbl-preview-editor">
            <span className="pbl-preview-tag">EDITOR</span>
            <label className="pbl-pe-row">
              <span>Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="pbl-pe-row">
              <span>Role</span>
              <input value={role} onChange={(e) => setRole(e.target.value)} />
            </label>
            <label className="pbl-pe-row">
              <span>Accent</span>
              <div className="pbl-pe-swatches">
                {['#ff69b4', '#e0a3ff', '#9370db', '#00ffff', '#ff8c00'].map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`pbl-pe-swatch ${hue === c ? 'active' : ''}`}
                    style={{ background: c }}
                    onClick={() => setHue(c)}
                    aria-label={`Accent ${c}`}
                  />
                ))}
              </div>
            </label>
          </div>

          <div className="pbl-preview-render" style={{ '--render-accent': hue }}>
            <span className="pbl-preview-tag pbl-preview-tag-mirror">LIVE PREVIEW</span>
            <div className="pbl-pr-paper">
              <div className="pbl-pr-band" />
              <div className="pbl-pr-name">{name || 'Your Name'}</div>
              <div className="pbl-pr-role">{role || 'Your Role'}</div>
              <div className="pbl-pr-divider" />
              <div className="pbl-pr-line w90" />
              <div className="pbl-pr-line w70" />
              <div className="pbl-pr-line w85" />
              <div className="pbl-pr-line w60" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Process flow ---------- */
const FLOW_STEPS = [
  { title: 'Complete your profile', body: 'Name, role, summary, work history, projects — done once, used everywhere.' },
  { title: 'Pick a template', body: 'Five hand-tuned looks: Minimal, Creative, Gallery, Magazine, Professional.' },
  { title: 'See it render live', body: 'WYSIWYG. Switch templates and your data re-flows in the same frame.' },
  { title: 'Customize the accent', body: 'One-click color swap — every section restyles in unison.' },
  { title: 'Save & get a public link', body: 'Auto-saved. You get a /p/your-handle link to share anywhere.' },
  { title: 'Update anytime', body: 'Edit the profile once. Every place your portfolio is shared updates automatically.' },
];

const FlowSection = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(0); }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (active < 0 || active >= FLOW_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="pbl-section" ref={ref}>
      <div className="pbl-section-head">
        <span className="pbl-eyebrow">HOW IT WORKS</span>
        <h2 className="pbl-h2">
          Six steps. <span className="pbl-grad-text">A live, sharable portfolio.</span>
        </h2>
        <p className="pbl-lead pbl-center">
          From "I should have a portfolio" to "here's the link" — under five minutes.
        </p>
      </div>
      <div className="pbl-flow-grid">
        {FLOW_STEPS.map((s, i) => (
          <div key={s.title} className={`pbl-flow-node ${i <= active ? 'lit' : ''}`}>
            <div className="pbl-flow-num">{String(i + 1).padStart(2, '0')}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < FLOW_STEPS.length - 1 && <div className={`pbl-flow-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- Stats ---------- */
const STATS = [
  { val: 5, suffix: '', label: 'Hand-tuned templates' },
  { val: 90, suffix: 's', label: 'From start to live link' },
  { val: 100, suffix: '%', label: 'Mobile-responsive' },
  { val: 80, suffix: 'K+', label: 'Portfolios published' },
];
const useCounter = (target, start) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isFloat = !Number.isInteger(target);
    const dur = 1400; const t0 = performance.now(); let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = target * eased;
      setVal(isFloat ? Math.round(v * 10) / 10 : Math.round(v));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return val;
};
const StatTile = ({ stat, start }) => {
  const v = useCounter(stat.val, start);
  return (
    <div className="pbl-stat">
      <div className="pbl-stat-num">{v}{stat.suffix}</div>
      <div className="pbl-stat-lbl">{stat.label}</div>
    </div>
  );
};
const StatsBand = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setStart(true); }),
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section className="pbl-stats-band" ref={ref}>
      <div className="pbl-stats-glow" />
      {STATS.map((s) => <StatTile key={s.label} stat={s} start={start} />)}
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQS = [
  { q: 'Where does the content come from?', a: 'From your Terview profile. The same data that powers your resume builder, cover letters, and interview answers also drives your portfolio — so everything stays in sync automatically.' },
  { q: 'Can I have a custom URL?', a: 'Yes. Every portfolio gets a clean public URL like /p/your-handle. You pick the slug. Share that link anywhere.' },
  { q: 'Will it work on mobile?', a: 'Every template is mobile-responsive by default. Recruiters viewing on a phone get the same polished experience as on desktop.' },
  { q: 'Can I switch templates after publishing?', a: 'Yes — instantly. Switch templates anytime and the public link automatically renders the new layout. No re-publishing required.' },
  { q: 'How do I update my portfolio?', a: 'Edit your Terview profile. Changes propagate to your live portfolio immediately. One source of truth, everywhere.' },
  { q: 'Can I add custom sections?', a: 'Yes — projects, certifications, skills, and a customizable highlights section are all editable. We keep the structure clean so it stays scannable.' },
];

const FaqAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="pbl-section">
      <div className="pbl-section-head">
        <span className="pbl-eyebrow">QUESTIONS, ANSWERED</span>
        <h2 className="pbl-h2">Everything you wanted to ask</h2>
      </div>
      <div className="pbl-faq-list">
        {FAQS.map((f, i) => (
          <button key={f.q} type="button" className={`pbl-faq-item ${open === i ? 'open' : ''}`} onClick={() => setOpen(open === i ? -1 : i)}>
            <div className="pbl-faq-q">
              <span>{f.q}</span>
              <span className="pbl-faq-chev">+</span>
            </div>
            <div className="pbl-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Main page ---------- */
const PortfolioBuilderLanding = () => {
  const navigate = useNavigate();
  const studioRef = useRef(null);
  const goStudio = useCallback(() => {
    if (studioRef.current) studioRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const setShowAuthModal = useCallback((show) => { if (show) navigate('/?openLogin=1'); }, [navigate]);

  const heroRef = useRef(null);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="pbl-page">
      <Header setShowAuthModal={setShowAuthModal} />

      <section className="pbl-hero" ref={heroRef}>
        <FloatingCardsCanvas />
        <div className="pbl-hero-bg" />
        <div className="pbl-hero-halo" aria-hidden="true" />
        <img src={HERO_IMG} alt="" className="pbl-hero-img" loading="eager" />

        <div className="pbl-hero-content">
          <span className="pbl-hero-pill">
            <span className="pbl-pill-dot" /> 5 templates · live preview · public share link
          </span>
          <h1 className="pbl-h1">
            A portfolio recruiters <span className="pbl-grad-text">click and remember</span>.
          </h1>
          <p className="pbl-hero-sub">
            Five hand-tuned templates. WYSIWYG live preview. Auto-saved, mobile-responsive,
            shareable at /p/your-handle. Built from your Terview profile so everything stays
            perfectly in sync.
          </p>

          <div className="pbl-hero-cta">
            <button className="pbl-btn pbl-btn-primary" onClick={goStudio}>
              <span>Build my portfolio</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="pbl-btn pbl-btn-ghost" onClick={() => navigate('/profile')}>
              Complete profile first
            </button>
          </div>

          <div className="pbl-hero-checks">
            <span>✓ 5 hand-tuned templates</span>
            <span>✓ WYSIWYG live preview</span>
            <span>✓ Public /p/handle link</span>
          </div>
        </div>

        {/* Floating glass mini-portfolio card */}
        <div className="pbl-floater" aria-hidden="true">
          <div className="pbl-floater-card">
            <div className="pbl-floater-band" />
            <div className="pbl-floater-name">Jane Doe</div>
            <div className="pbl-floater-role">Senior Software Engineer · 8y</div>
            <div className="pbl-floater-bar w85" />
            <div className="pbl-floater-bar w65" />
            <div className="pbl-floater-bar w90" />
            <div className="pbl-floater-link">↗ /p/janedoe</div>
          </div>
          <div className="pbl-floater-glow" />
        </div>
      </section>

      <section className="pbl-studio" ref={studioRef} id="studio">
        <div className="pbl-studio-head">
          <span className="pbl-eyebrow">PORTFOLIO STUDIO</span>
          <h2 className="pbl-h2">
            Build it now — <span className="pbl-grad-text">live, in this page</span>.
          </h2>
          <p className="pbl-lead pbl-center">
            Pick a template, watch it render, save, and grab your shareable /p/handle link.
          </p>
        </div>
        <PortfolioPage embedded />
      </section>

      <TemplateMorphDemo />
      <LivePreviewDemo />

      <section className="pbl-section">
        <div className="pbl-section-grid">
          <div className="pbl-section-text">
            <span className="pbl-eyebrow">SHARE-ANYWHERE LINK</span>
            <h2 className="pbl-h2">
              One link. <span className="pbl-grad-text">Every channel.</span>
            </h2>
            <p className="pbl-lead">
              Drop /p/your-handle in your LinkedIn headline, your email signature, your business
              card, your Twitter bio. One link, always up to date — because it pulls from your
              Terview profile in real time.
            </p>
            <ul className="pbl-bullet-list">
              <li><span className="pbl-dot" />Custom slug — pick your own handle</li>
              <li><span className="pbl-dot" />SEO-optimized for recruiter searches</li>
              <li><span className="pbl-dot" />Open Graph preview cards built-in</li>
            </ul>
          </div>
          <div className="pbl-img-frame">
            <img src={SHARE_IMG} alt="Share-anywhere link" />
            <div className="pbl-img-frame-glow" />
          </div>
        </div>
      </section>

      <FlowSection />
      <StatsBand />

      <section className="pbl-section">
        <div className="pbl-section-grid pbl-flip">
          <div className="pbl-img-frame">
            <img src={SHELF_IMG} alt="Five templates" />
            <div className="pbl-img-frame-glow" />
          </div>
          <div className="pbl-section-text">
            <span className="pbl-eyebrow">FIVE TEMPLATES</span>
            <h2 className="pbl-h2">
              Pick the one that <span className="pbl-grad-text">fits your room</span>.
            </h2>
            <p className="pbl-lead">
              A senior engineer applying to BigCo? Professional. A designer pitching a studio?
              Creative. A student showing off projects? Gallery. A writer? Magazine. Each
              template is hand-tuned for its audience.
            </p>
          </div>
        </div>
      </section>

      <section className="pbl-section">
        <div className="pbl-section-grid">
          <div className="pbl-section-text">
            <span className="pbl-eyebrow">LIVE WYSIWYG</span>
            <h2 className="pbl-h2">
              Edit on one side. <span className="pbl-grad-text">Watch the magic</span> on the other.
            </h2>
            <p className="pbl-lead">
              Real-time preview means no surprises. Every keystroke, every template switch,
              every accent change — you see exactly what your visitors will see, before you ship.
            </p>
          </div>
          <div className="pbl-img-frame">
            <img src={PREVIEW_IMG} alt="Live preview" />
            <div className="pbl-img-frame-glow" />
          </div>
        </div>
      </section>

      <FaqAccordion />

      <section className="pbl-cta-final">
        <div className="pbl-cta-card">
          <div className="pbl-cta-glow" />
          <h2 className="pbl-h2">
            Stop telling people <span className="pbl-grad-text">where to find your work</span>.
          </h2>
          <p>Send the link. Make the portfolio do the work.</p>
          <div className="pbl-hero-cta center">
            <button className="pbl-btn pbl-btn-primary" onClick={goStudio}>
              <span>Build my portfolio</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="pbl-btn pbl-btn-ghost" onClick={() => navigate('/profile')}>Complete profile first</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PortfolioBuilderLanding;
